import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Divider,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  QrCode as QrCodeIcon,
  Sms as SmsIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  ChevronRight as ChevronForwardIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import PersonalDetailsuwin from "./PersonalDetailsuwin";
import { decryptOpenSSLAES } from "./uwin-decrypt";
import { formatDobForAPI, getBeneficiaryAgeDisplay, getVisiblePhotoIdSuffix } from "../../../helpers/common-uwinhelper";
import { toast } from "react-toastify";
import { uwinAPI } from "../../../services/uwin.api";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { encryptText, getUwinClientToken } from "../../../helpers/common-uwinhelper.js";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AadhaarEntryForm from './AadhaarEkyc/AadhaarEntryForm';
import AadhaarOtpAuth from './AadhaarEkyc/AadhaarOtpAuth';
import AadhaarDemographicAuth from './AadhaarEkyc/AadhaarDemographicAuth';
import { usePhotoId } from "./usePhotoId";

const MemberDrawer = ({ toggleDrawer, beneficiaryData, onSwitchToVaccination, onComplete }) => {
  
  // Step management
  const [currentStep, setCurrentStep] = useState(2); // Start at Step 2: Identification
  const [txnID, setTxnID] = useState(null);
  
  // Step configuration
  const stepConfig = {
    2: { 
      title: "Identification", 
      next: "Personal Details",
      showTabs: true 
    },
    3: { 
      title: "Personal Details", 
      next: "Vaccination Details",
      showTabs: false 
    },
     4: { title: "Member Verification", next: "Vaccination", showTabs: false },
  };

  // Navigation functions
  const goToNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 2 && currentStep < 4) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const [showPersonalDetailsuwin, setShowPersonalDetailsuwin] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0); // 0 for Manual, 1 for Aadhaar eKYC
  const [authenticationType, setAuthenticationType] = useState("");
  const fileInputRef = useRef(null);

  // Manual verification states
  const [photoIdTypes, setPhotoIdTypes] = useState([]);
  const [selectedPhotoIdType, setSelectedPhotoIdType] = useState("");
  const [photoIdNumber, setPhotoIdNumber] = useState("");
  const [isPhotoIdTypeDisabled, setIsPhotoIdTypeDisabled] = useState(false);
  const [isPhotoIdNumberDisabled, setIsPhotoIdNumberDisabled] = useState(false);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);
  const [showPhotoIdNumber, setShowPhotoIdNumber] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // Add state for selected file
  const [filePreview, setFilePreview] = useState(null); // Add state for file preview
  const [existingFileUrl, setExistingFileUrl] = useState(null); // State for already-uploaded file URL from server

  // Aadhaar eKYC states
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [ekycStep, setEkycStep] = useState("entry");

  const {
    handlePhotoIdNumberChange,
    validatePhotoIdNumber,
    photoIdError,
    setPhotoIdError,
  } = usePhotoId(photoIdTypes, selectedPhotoIdType, photoIdNumber, setPhotoIdNumber);

console.log(beneficiaryData,"beneficiaryData")
  useEffect(() => {
    if (beneficiaryData) {
      // Reset manual verification states first to avoid state leakage
      setSelectedPhotoIdType("");
      setPhotoIdNumber("");
      setPhotoIdError("");
      setIsPhotoIdTypeDisabled(false);
      setIsPhotoIdNumberDisabled(false);
      setIsUploadDisabled(false);
      setSelectedFile(null);
      setFilePreview(null);
      setExistingFileUrl(null);

      fetchPhotoIdTypes();
      setupAadhaarTab();
    }
  }, [beneficiaryData]);

  const setupAadhaarTab = () => {
    if (!beneficiaryData) return;
    setAadhaarNumber("");
  };

  const fetchPhotoIdTypes = async () => {
    const dobToUse = beneficiaryData?.guardian_dob || beneficiaryData?.dob;
    if (!dobToUse) {
      console.error("DOB not found in beneficiary data");
      return;
    }

    try {
      setIsLoading(true);
      const formattedDob = formatDobForAPI(dobToUse);
      
      if (!formattedDob) {
        toast.error("Invalid date format");
        return;
      }

      const response = await uwinAPI.getPhotoIdsByDob({ dob: formattedDob });

      if (response?.success && response?.data) {
        setPhotoIdTypes(response.data);

        // Only pre-fill and freeze both fields when BOTH photo_id_type AND
        // photo_id_number are present in the beneficiary data.
        // If either is missing the data is incomplete in the backend, so both
        // fields must stay empty and editable for manual entry.
        const hasBothPhotoIdFields =
          !!beneficiaryData.photo_id_type && !!beneficiaryData.photo_id_number;

        if (hasBothPhotoIdFields) {
          // Decrypt the stored type and find its matching dropdown option
          let decryptedPhotoIdType = "";
          try {
            decryptedPhotoIdType = decryptOpenSSLAES(beneficiaryData.photo_id_type);
          } catch (error) {
            console.error("Error decrypting photo_id_type:", error);
          }

          if (decryptedPhotoIdType) {
            const matchedPhotoIdType = response.data.find((item) => {
              const itemPhotoType = item.photo_type?.toLowerCase().trim();
              const decType = decryptedPhotoIdType?.toLowerCase().trim();
              return itemPhotoType === decType || item.type === beneficiaryData.photo_id_type;
            });

            if (matchedPhotoIdType) {
              // Pre-select the type dropdown so the user can see what is
              // registered, and pre-fill the number (even if masked) and freeze it.
              setSelectedPhotoIdType(matchedPhotoIdType.type);
              setIsPhotoIdTypeDisabled(true);

              if (beneficiaryData.photo_id_number) {
                try {
                  const decryptedNumber = decryptOpenSSLAES(beneficiaryData.photo_id_number);
                  setPhotoIdNumber(decryptedNumber);
                  setIsPhotoIdNumberDisabled(true);
                } catch (error) {
                  console.error("Error decrypting photo_id_number:", error);
                }
              }
            }
          }
        }
        // If only one of the two is present, or neither is present, both
        // fields remain at their default empty/editable state.

        // Set existing file URL from server if available (independent of above)
        if (beneficiaryData.photo_id_file) {
          try {
            const res = await uwinAPI.downloadFile({ fileUrl: beneficiaryData.photo_id_file });
            
            // If the response is a blob, we can create an object URL directly from it.
            if (res instanceof Blob) {
              setExistingFileUrl(URL.createObjectURL(res));
            } else {
              setExistingFileUrl(beneficiaryData.photo_id_file);
            }
          } catch(error) {
            console.error("Error downloading file:", error);
            setExistingFileUrl(beneficiaryData.photo_id_file);
          }
        }
      } else {
        toast.error(response?.message || "Failed to fetch photo ID types");
      }
    } catch (error) {
      console.error("Error fetching photo ID types:", error);
      toast.error("Server error while fetching photo ID types");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (newValue === 1) {
      setEkycStep("entry");
    }
  };

  const handleAuthenticationChange = (event) => {
    setAuthenticationType(event.target.value);
  };

  const handlePhotoIdTypeChange = (event) => {
    const selectedType = event.target.value;
    setSelectedPhotoIdType(selectedType);
    setPhotoIdNumber("");
    setPhotoIdError("");

    // Find the selected photo ID type object
    const selectedTypeObj = photoIdTypes.find((item) => item.type === selectedType);
    
    // Check if upload is required
    if (selectedTypeObj && selectedTypeObj.upload_required === "No") {
      setIsUploadDisabled(true);
    } else {
      setIsUploadDisabled(false);
    }
  };

  const handleFileUpload = () => {
    // Always allow file upload regardless of upload requirements
    fileInputRef.current?.click();
  };

    const handleFileChange = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
          toast.error("Please upload PNG, JPEG, or PDF files only");
          return;
        }

        // Validate file size (2MB max)
        const maxSize = 2 * 1024 * 1024; // 2MB in bytes
        if (file.size > maxSize) {
          toast.error("File size must be less than 2MB");
          return;
        }

        // Validate file name length (50 characters max)
        if (file.name.length > 50) {
          toast.error("File name must not exceed 50 characters");
          return;
        }

        setSelectedFile(file);
        
        // Create preview for images
        if (file.type.startsWith('image/')) {
          setFilePreview(URL.createObjectURL(file));
        } else {
          setFilePreview(null); // For PDFs, no preview
        }

        console.log("File selected:", file.name, "Type:", file.type, "Size:", file.size);
      }
    };

    const handleFileRemove = (event) => {
    // Prevent event from bubbling to Paper onClick
    event?.stopPropagation();
    
    // Clear file and preview
    setSelectedFile(null);
    setFilePreview(null);
    setExistingFileUrl(null);
    
    // Reset the file input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    console.log("File removed");
  };

  const handlePdfDownload = (url, filename = "photo-id.pdf") => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  };

  const isExistingPdf = beneficiaryData?.photo_id_file
    ?.split("?")[0]
    .toLowerCase()
    .endsWith(".pdf");

  const togglePhotoIdVisibility = () => {
    setShowPhotoIdNumber(!showPhotoIdNumber);
  };

  // Handle continue from identification step
  const handleContinueFromIdentification = async () => {
    if (selectedTab === 0) {
      // Validate required data for Manual Verification
      if (!selectedPhotoIdType) {
        toast.error("Please select a photo ID type");
        return;
      }

      if (!photoIdNumber) {
        toast.error("Please enter photo ID number");
        return;
      }

      // Perform validation check for the selected Photo ID Type
      const isPrepopulated = isPhotoIdNumberDisabled || (!!beneficiaryData?.photo_id_type && !!beneficiaryData?.photo_id_number);
      const validation = validatePhotoIdNumber(isPrepopulated);
      if (!validation.isValid) {
        toast.error(validation.errorMsg || "Invalid Photo ID format");
        return;
      }

      // Generate OTP first, then move to next step only if successful
      const otpSuccess = await generateOTP();

      if (otpSuccess) {
        goToNextStep();
      }
      // If OTP generation fails, user stays on current step with error message
    } else {
      // Aadhaar eKYC Step
      if (!aadhaarNumber || aadhaarNumber.length < 12) {
        toast.error("Please enter a valid 12-digit Aadhaar Number");
        return;
      }
      if (!authenticationType) {
        toast.error("Please select an authentication type");
        return;
      }

      setEkycStep(authenticationType);
    }
  };

  const handleEkycAuthComplete = () => {
    // Called when the inner eKYC component (OTP or Demographic) completes successfully
    goToNextStep();
  };

  const handleEkycBack = () => {
    // Go back to the entry form
    setEkycStep("entry");
  };

  // Helper function to prepare identification data for PersonalDetailsuwin
  const getIdentificationData = () => {
    // Signal to PersonalDetailsuwin that both fields already exist in the
    // backend — it should skip appending them to the submission payload.
    const hasBothPhotoIdFields =
      !!beneficiaryData?.photo_id_type && !!beneficiaryData?.photo_id_number;
    return {
      selectedPhotoIdType,
      photoIdNumber,
      selectedFile,
      existingFileUrl,
      isUploadDisabled,
      hasBothPhotoIdFields,
    };
  };

  // Handle continue from personal details (from PersonalDetailsuwin component)
  const handleContinueFromPersonalDetails = () => {
    // Add logic for next step or close drawer
    goToNextStep(); 
  };

 const goToMemberVaccination = () => {
    if (onSwitchToVaccination) {
      onSwitchToVaccination();
    }
  }
  
  const generateOTP = async() => {
    try {
      setIsOtpLoading(true);

      const payload = {
        secret: "U2FsdGVkX18HdWQ1/gucdqpSkSOb57nZhGiwIsEZHoQguLgMnXY0z0d38+CVfuih5tQdZLgRNUNWV5WNOU7fyA==",
        mobile: beneficiaryData?.ben_mobile_number,
        beneficiary_id: beneficiaryData?.beneficiary_id,
        session_id: beneficiaryData?.session_id
      };

      const response = await uwinAPI.generateBenOTP(payload);

      if (response?.success && response?.data?.txnId) {
        setTxnID(response.data.txnId);
      } 
    } catch (error) {
      console.error("OTP generation error:", error);

      // Show backend errors as-is
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      }
      // return false;
    } finally {
      setIsOtpLoading(false);
       return true;
    }
  }

  const ProgressCircle = ({ current, total }) => {
    const percentage = (current / total) * 100;

    return (
      <Box sx={{ position: "relative", width: 60, height: 60 }}>
        {/* Background circle */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={60}
          thickness={4}
          sx={{ color: "#e0e0e0", position: "absolute" }}
        />
        {/* Progress circle */}
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={60}
          thickness={4}
          sx={{ color: "#1976d2", position: "absolute" }}
        />
        {/* Center text */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" fontWeight="bold" fontSize="12px">
            {current}/{total}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
    className="memberDrawerContainer"  sx={{
    width: {
      xs: "100%",   // mobile
      sm: 420,      // small tablet
      md: 490       // laptop
    },
    height: "100vh",
    bgcolor: "white"
  }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Left side - Back button and title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Back button - only show on step 3 (not on first step or step 4 Member Verification) */}
            {currentStep > 2 && currentStep < 4 && (
              <IconButton 
                onClick={goToPreviousStep} 
                size="small"
                sx={{ mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
          <Typography variant="h6" fontWeight="bold">
            Member Verification
          </Typography>
          </Box>
          
          {/* Close button - hidden on step 4 */}
          {currentStep !== 4 && (
            <IconButton 
              onClick={() => {
                toggleDrawer(false)();
              }} 
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Dynamic Progress Header */}
      <Box sx={{ p: 3, bgcolor: "#f8f9fa" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <ProgressCircle current={currentStep} total={4} />
          </Grid>
          <Grid item xs={9}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {stepConfig[currentStep]?.title || "Unknown Step"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Next: {stepConfig[currentStep]?.next || "Unknown"}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Tab Navigation - Only show for Identification step */}
      {stepConfig[currentStep]?.showTabs && (
        <>
      <Box sx={{ px: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Manual Verification" value={0} />
          {/* Aadhaar eKYC is still under development - keep the tab disabled */}
          <Tab label="Aadhaar eKYC" value={1} disabled />
        </Tabs>
      </Box>
      <Divider sx={{ mx: 3, mt: 1 }} />
        </>
      )}

      {/* Content Area */}
      <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
        {/* Step 2: Identification */}
        {currentStep === 2 && (
          <>
            {/* Manual Verification Tab */}
            {selectedTab === 0 && (
              <Box>
                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box component="form" sx={{ width: "100%" }}>
                    {/* Photo ID Type Selection */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="body2"
                        color="primary"
                        fontWeight="500"
                        gutterBottom
                      >
                        Photo ID Type *
                      </Typography>
                      <FormControl fullWidth variant="outlined" disabled={isPhotoIdTypeDisabled}>
                        <Select
                          value={selectedPhotoIdType}
                          onChange={handlePhotoIdTypeChange}
                          displayEmpty
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#e0e0e0",
                            },
                            "&.Mui-disabled": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#e0e0e0",
                              },
                            },
                          }}
                        >
                          {!selectedPhotoIdType && (
                            <MenuItem value="" disabled>
                              Select Photo ID Type
                            </MenuItem>
                          )}
                          {photoIdTypes.map((type) => (
                            <MenuItem key={type.type} value={type.type}>
                              {type.photo_type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Photo ID Number */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="body2"
                        color="primary"
                        fontWeight="500"
                        gutterBottom
                      >
                        Photo ID Number *
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 0,
                          borderRadius: 1,
                          border: `1px solid ${photoIdError ? "#d32f2f" : "#e0e0e0"}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <TextField
                            placeholder="Photo ID Number"
                            type={showPhotoIdNumber ? "text" : "password"}
                            value={photoIdNumber}
                            onChange={handlePhotoIdNumberChange}
                            required
                            disabled={isPhotoIdNumberDisabled}
                            error={!!photoIdError}
                            fullWidth
                            variant="standard"
                            inputProps={{
                              maxLength: 20,
                              style: { padding: "12px 16px" },
                            }}
                            sx={{
                              "& .MuiInput-underline:before": { display: "none" },
                              "& .MuiInput-underline:after": { display: "none" },
                              "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                                { display: "none" },
                            }}
                          />
                          <IconButton 
                            sx={{ p: 1, mr: 1 }}
                            onClick={togglePhotoIdVisibility}
                          >
                            {showPhotoIdNumber ? (
                              <VisibilityIcon sx={{ fontSize: 20, color: "#666" }} />
                            ) : (
                              <VisibilityOffIcon sx={{ fontSize: 20, color: "#666" }} />
                            )}
                          </IconButton>
                        </Box>
                      </Paper>
                      {photoIdError && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {photoIdError}
                        </Typography>
                      )}
                    </Box>

                    {/* File Upload Section */}
                    <Box sx={{ mb: 4 }}>
                      <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        accept="image/png,image/jpeg,application/pdf"
                        onChange={handleFileChange}
                      />
                      
                      <Paper
                        elevation={0}
                        sx={{
                          border: "2px dashed #e0e0e0",
                          borderRadius: 2,
                          p: 4,
                          textAlign: "center",
                          cursor: (selectedFile || existingFileUrl) ? "default" : "pointer",
                          "&:hover": {
                            borderColor: (selectedFile || existingFileUrl) ? "#e0e0e0" : "#1976d2",
                            bgcolor: (selectedFile || existingFileUrl) ? "white" : "#f8f9fa",
                          },
                        }}
                        onClick={(selectedFile || existingFileUrl) ? null : handleFileUpload}
                      >
                        {(!selectedFile && !existingFileUrl) ? (
                          // Initial upload state - no file selected
                          <>
                            <Box sx={{ mb: 2 }}>
                              <CloudUploadIcon sx={{ fontSize: 48, color: "#666" }} />
                            </Box>
                            <Typography variant="h6" fontWeight="500" gutterBottom>
                              Upload Photo ID
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Click & Upload
                            </Typography>
                          </>
                        ) : (
                          // File present - newly selected or previously uploaded from server
                          <Box>
                            {/* New file selected by user — image */}
                            {selectedFile && selectedFile.type.startsWith('image/') && (
                              <Box sx={{ position: 'relative', width: '100%' }}>
                                <Box
                                  component="img"
                                  src={filePreview}
                                  alt="Photo ID Preview"
                                  sx={{
                                    width: '100%',
                                    maxHeight: 300,
                                    objectFit: 'contain',
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                  }}
                                />
                                <IconButton
                                  onClick={handleFileRemove}
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: 'error.dark' },
                                    boxShadow: 2,
                                  }}
                                  size="small"
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}

                            {/* New file selected by user — PDF */}
                            {selectedFile && !selectedFile.type.startsWith('image/') && (
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <Box
                                  sx={{
                                    width: 200,
                                    height: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                    gap: 2,
                                  }}
                                >
                                  <PictureAsPdfIcon
                                    sx={{ fontSize: 48, color: '#d32f2f', cursor: 'pointer' }}
                                    onClick={() => handlePdfDownload(URL.createObjectURL(selectedFile), selectedFile.name)}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ px: 2, wordBreak: 'break-word', textAlign: 'center', fontSize: '12px' }}
                                  >
                                    {selectedFile.name}
                                  </Typography>
                                </Box>
                                <IconButton
                                  onClick={handleFileRemove}
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: 'error.dark' },
                                    boxShadow: 2,
                                  }}
                                  size="small"
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}

                            {/* Previously uploaded file from server */}
                            {!selectedFile && existingFileUrl && isExistingPdf && (
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <Box
                                  sx={{
                                    width: 200,
                                    height: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                    gap: 2,
                                  }}
                                >
                                  <PictureAsPdfIcon
                                    sx={{ fontSize: 48, color: '#d32f2f', cursor: 'pointer' }}
                                    onClick={() => handlePdfDownload(
                                      existingFileUrl,
                                      beneficiaryData.photo_id_file.split('/').pop()?.split('?')[0] || 'photo-id.pdf'
                                    )}
                                  />
                                  <Typography variant="body2" sx={{ px: 2, fontSize: '12px' }}>
                                    PDF Document
                                  </Typography>
                                </Box>
                                <IconButton
                                  onClick={handleFileRemove}
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: 'error.dark' },
                                    boxShadow: 2,
                                  }}
                                  size="small"
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}
                            {!selectedFile && existingFileUrl && !isExistingPdf && (
                              <Box sx={{ position: 'relative', width: '100%' }}>
                                <Box
                                  component="img"
                                  src={existingFileUrl}
                                  alt="Photo ID Preview"
                                  sx={{
                                    width: '100%',
                                    maxHeight: 300,
                                    objectFit: 'contain',
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                  }}
                                />
                                <IconButton
                                  onClick={handleFileRemove}
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: 'error.dark' },
                                    boxShadow: 2,
                                  }}
                                  size="small"
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}

                            {/* File info caption */}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                              {selectedFile
                                ? `${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)`
                                : 'Previously uploaded photo ID'
                              }
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Box>

                    {/* Continue Button */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!photoIdNumber || !selectedPhotoIdType || isOtpLoading}
                        sx={{
                          borderRadius: 3,
                          py: 1.5,
                          textTransform: "none",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                        onClick={handleContinueFromIdentification}
                      >
                        {isOtpLoading && <CircularProgress size={20} color="inherit" />}
                        {isOtpLoading ? "Generating OTP..." : "Continue"}
                        {!isOtpLoading && <ChevronForwardIcon sx={{ fontSize: 20 }} />}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Aadhaar eKYC Tab */}
            {selectedTab === 1 && (
              <Box>
                {ekycStep === "entry" && (
                  <AadhaarEntryForm
                    aadhaarNumber={aadhaarNumber}
                    setAadhaarNumber={setAadhaarNumber}
                    authenticationType={authenticationType}
                    setAuthenticationType={setAuthenticationType}
                    onContinue={handleContinueFromIdentification}
                    isLoading={isOtpLoading}
                  />
                )}
                {ekycStep === "OTP" && (
                  <AadhaarOtpAuth 
                    onBack={handleEkycBack}
                    onVerify={handleEkycAuthComplete}
                    aadhaarNumber={aadhaarNumber}
                  />
                )}
                {ekycStep === "Demographic" && (
                  <AadhaarDemographicAuth 
                    onBack={handleEkycBack}
                    onVerify={handleEkycAuthComplete}
                    aadhaarNumber={aadhaarNumber}
                  />
                )}
              </Box>
            )}
          </>
        )}

        {/* Step 3: Personal Details */}
        {currentStep === 3 && (
          <PersonalDetailsuwin
            beneficiaryData={beneficiaryData}
            identificationData={getIdentificationData()} // Pass identification data
            onContinue={handleContinueFromPersonalDetails}
            onBack={goToPreviousStep} // Pass back function
            txnID={txnID}
            onResendOTP={generateOTP}
          />
        )}

        {/* Step 4: Member Verification Success */}
      {currentStep === 4 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Member Verified Successfully
          </Typography>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>NAME</Typography>
            <Typography variant="body1" fontWeight={500}>{beneficiaryData?.ben_name ?? "NA"}</Typography>
            
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{mt:2}}>AGE / GENDER</Typography>
            <Typography variant="body1">{getBeneficiaryAgeDisplay(beneficiaryData)} / {beneficiaryData?.beneficiary_gender ?? "NA"}</Typography>
            
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{mt:2}}>MOBILE NUMBER</Typography>
            <Typography variant="body1">{beneficiaryData?.ben_mobile_number ? decryptOpenSSLAES(beneficiaryData.ben_mobile_number) : "NA"}</Typography>
            
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{mt:2}}>PHOTO ID</Typography>
            <Typography variant="body1">
              {beneficiaryData?.photo_id_type ? decryptOpenSSLAES(beneficiaryData.photo_id_type) : "NA"}/
              {beneficiaryData?.photo_id_number
                ? getVisiblePhotoIdSuffix(decryptOpenSSLAES(beneficiaryData.photo_id_number))
                : "NA"}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{mt:2}}>CATEGORY</Typography>
            <Typography variant="body1">{beneficiaryData?.current_beneficiary_type_name ?? "NA"}</Typography>
          </Box>
          
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 4, borderRadius: 3, py: 1.5 }}
            onClick={goToMemberVaccination}
          >
            Continue <ChevronForwardIcon />
          </Button>
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default MemberDrawer;